import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Collapse,
    Divider,
    IconButton,
    Paper,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import {
    CheckCircle as HealthyIcon,
    ErrorOutline as CriticalIcon,
    ExpandLess,
    ExpandMore,
    HelpOutline as UnavailableIcon,
    Refresh as RefreshIcon,
    WarningAmber as DegradedIcon,
} from '@mui/icons-material';
import operatorConsoleService from '../../services/operatorConsoleService';

// Platform Overview — the operator console's top-level view (§20.1).
//
// The mandate's instruction shapes this page more than any styling choice: the
// console "must not be a collection of raw database metrics" and "should behave
// like an infrastructure control plane". So every card leads with a SENTENCE and
// carries the number as support, rather than the other way round. "412" is a
// metric; "replication is 412 parts behind on the busiest replica" is something an
// operator can act on.
//
// The second decision worth knowing about is that UNAVAILABLE is rendered, not
// hidden. Half of §20.1's signals cannot be measured on this platform yet, and a
// page that quietly dropped them would look complete and healthy while saying
// nothing about the parts nobody is watching.

const STATUS_META = {
    HEALTHY: { icon: HealthyIcon, colour: 'success', label: 'Healthy' },
    DEGRADED: { icon: DegradedIcon, colour: 'warning', label: 'Degraded' },
    CRITICAL: { icon: CriticalIcon, colour: 'error', label: 'Critical' },
    // Deliberately neutral rather than red. An unmeasured signal is not a failure
    // of the system; it is a gap in what we can see, and colouring it as an
    // incident would train operators to ignore the colour.
    UNAVAILABLE: { icon: UnavailableIcon, colour: 'default', label: 'Not measured' },
};

const SignalCard = ({ signal }) => {
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    const meta = STATUS_META[signal.status] || STATUS_META.UNAVAILABLE;
    const Icon = meta.icon;
    const hasDetail = signal.detail && Object.keys(signal.detail).length > 0;
    const unmeasured = signal.status === 'UNAVAILABLE';

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                // Unmeasured cards recede rather than disappear.
                opacity: unmeasured ? 0.72 : 1,
                borderColor:
                    signal.status === 'CRITICAL'
                        ? theme.palette.error.main
                        : signal.status === 'DEGRADED'
                          ? theme.palette.warning.main
                          : undefined,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Icon fontSize="small" color={meta.colour === 'default' ? 'disabled' : meta.colour} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, flexGrow: 1 }}>
                    {signal.label}
                </Typography>
                <Chip size="small" label={meta.label} color={meta.colour} variant="outlined" />
            </Box>

            {/* The number is support, not the headline — hence secondary placement
                and a dash rather than a 0 when nothing was measured. */}
            <Typography variant="h5" sx={{ fontWeight: 600, lineHeight: 1.1 }}>
                {signal.value === null || signal.value === undefined ? '—' : String(signal.value)}
                {signal.unit && signal.value !== null && signal.value !== undefined && (
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                        {signal.unit}
                    </Typography>
                )}
            </Typography>

            <Typography variant="body2" color="text.secondary">
                {signal.summary}
            </Typography>

            {hasDetail && (
                <>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <IconButton size="small" onClick={() => setOpen((v) => !v)}>
                            {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                        </IconButton>
                    </Box>
                    <Collapse in={open} unmountOnExit>
                        <Box
                            component="pre"
                            sx={{
                                m: 0,
                                p: 1,
                                fontSize: 11,
                                overflowX: 'auto',
                                bgcolor: theme.palette.action.hover,
                                borderRadius: 1,
                            }}
                        >
                            {JSON.stringify(signal.detail, null, 2)}
                        </Box>
                    </Collapse>
                </>
            )}
        </Paper>
    );
};

const OperatorOverviewPage = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            setData(await operatorConsoleService.overview());
        } catch (e) {
            setError(e?.response?.data?.error || e.message || 'Could not load the overview.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        // Thirty seconds, matching the dashboard cache. Faster would poll the
        // system tables harder than the information changes.
        const t = setInterval(load, 30000);
        return () => clearInterval(t);
    }, [load]);

    if (loading && !data) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    const health = data?.overallHealth || {};
    const healthMeta = STATUS_META[health.status] || STATUS_META.UNAVAILABLE;
    const HealthIcon = healthMeta.icon;

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        Platform Overview
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Control-plane health across ingestion, storage, schema, isolation and
                        alerting.
                    </Typography>
                </Box>
                <Tooltip title="Refresh">
                    <IconButton onClick={load} disabled={loading}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* The headline judgement, and — just as importantly — how much of the
                platform it is NOT speaking for. */}
            <Paper
                variant="outlined"
                sx={{ p: 2.5, mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}
            >
                <HealthIcon
                    fontSize="large"
                    color={healthMeta.colour === 'default' ? 'disabled' : healthMeta.colour}
                />
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {healthMeta.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {health.summary}
                    </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                    <Chip
                        size="small"
                        label={`${health.measuredSignals ?? 0} measured`}
                        sx={{ mr: 1 }}
                    />
                    {(health.unmeasuredSignals ?? 0) > 0 && (
                        <Chip
                            size="small"
                            variant="outlined"
                            label={`${health.unmeasuredSignals} not measured`}
                        />
                    )}
                </Box>
            </Paper>

            {(data?.unavailable ?? 0) > 0 && (
                // Stated once at the top rather than repeated on every card. It is
                // the sentence that stops a mostly-grey page being read as a
                // mostly-fine platform.
                <Alert severity="info" sx={{ mb: 3 }}>
                    {data.note}
                </Alert>
            )}

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        lg: 'repeat(3, 1fr)',
                    },
                    gap: 2,
                }}
            >
                {(data?.signals || []).map((s) => (
                    <SignalCard key={s.id} signal={s} />
                ))}
            </Box>

            <Divider sx={{ my: 3 }} />
            <Typography variant="caption" color="text.secondary">
                Query throughput and refusal counts are per backend instance; four instances run
                behind the load balancer.
            </Typography>
        </Box>
    );
};

export default OperatorOverviewPage;
